import { computed, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, updateAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withDevtools } from '@ngrx-toolkit/core';
import { EMPTY, mergeMap, pipe, switchMap, tap } from 'rxjs';

import { NotificationData } from '../../../definitions/interfaces/notifications/notification-data';
import { NotificationUpdateData } from '../../../definitions/interfaces/notifications/notification-update-data';
import { NotificationsService } from '../../../services/notifications/notifications.service';
import { ResiSnackbarService } from '../../../services/resi-snackbar/resi-snackbar.service';
import { LayoutStoreFacade } from '../../global-store/layout/layout-store.facade';

export const NotificationsStoreService = signalStore(
  { providedIn: 'root' },
  withDevtools('Notifications Store'),
  withState<{ loading: boolean }>({ loading: true }),
  withEntities<NotificationData>(),
  withComputed(store => ({
    isLoading: computed(() => store.loading()),
    hasNotifications: computed(() => store.ids().length > 0),
  })),
  withMethods(store => {
    const notificationsService = inject(NotificationsService);
    const snackbarService = inject(ResiSnackbarService);
    const layoutFacade = inject(LayoutStoreFacade);
    return {
      loadNotifications: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap(() =>
            notificationsService.getNotifications().pipe(tapResponse({
              next: (result: Array<NotificationData>) => {
                layoutFacade.loadUnreadNotificationsCount();
                patchState(store, setAllEntities(result), { loading: false });
              },
              error: () => {
                snackbarService.showGeneralError();
                patchState(store, { loading: false });
              }
            }))
          )
        )
      ),
      updateNotification: rxMethod<NotificationUpdateData>(
        pipe(
          mergeMap((data: NotificationUpdateData) =>
            notificationsService.updateNotifications([ data ]).pipe(tapResponse({
              next: () => {
                patchState(store, updateEntity({ id: data.notificationId, changes: { isRead: data.isRead }}));
                if (data.isRead) {
                  layoutFacade.decrementUnreadNotificationCountByOne();
                } else {
                  layoutFacade.incrementUnreadNotificationCountByOne();
                }
              },
              error: () => snackbarService.showGeneralError()
            }))
          )
        )
      ),
      markAllAsRead: rxMethod<void>(
        pipe(
          switchMap(() => {
            const nonReadNotificationIds: Array<NotificationUpdateData> = [];
            const allNonReadNotifications = store.entities()
              .filter((notification: NotificationData) => !notification.isRead);
            const allNonReadNotificationIds = allNonReadNotifications
              .map((notification: NotificationData) => notification.id);
            allNonReadNotificationIds.forEach((notificationId: string) => {
              nonReadNotificationIds.push({ notificationId, isRead: true });
            });
            if (nonReadNotificationIds?.length > 0) {
              return notificationsService.updateNotifications(nonReadNotificationIds).pipe(tapResponse({
                next: () => {
                  patchState(store, updateAllEntities({ isRead: true }));
                  layoutFacade.setUnreadNotificationToZero();
                },
                error: () => snackbarService.showGeneralError()
              }));
            } else {
              return EMPTY;
            }
          })
        )
      )
    };
  })
);
